import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastService } from '@irembo-andela/irembogov3-common';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import {
  BlockStateMode,
  IBlockedStatesConfig,
} from '../../../../core/models/block-state-config.model';

@Component({
  selector: 'irembogov-restrictions-configuration',
  templateUrl: './configuration-restrictions.component.html',
  styleUrls: ['./configuration-restrictions.component.scss'],
})
export class ConfigurationRestrictionsComponent implements OnInit, OnChanges {
  @Input() changeRequestJsonWorkflowData!: Record<string, unknown>;
  @Input() changeRequest!: IServiceChangeRequest;
  @Output() workflowUpdated: EventEmitter<Record<string, unknown>> =
    new EventEmitter<Record<string, unknown>>();

  restrictionsForm!: FormGroup;
  newStateInput = '';

  // Expose enum to template
  BlockStateMode = BlockStateMode;

  // Common application states (for suggestions)
  commonApplicationStates: string[] = [
    'PENDING_RESUBMISSION',
    'SUBMITTED',
    'APPROVED',
    'PENDING_OFFICER_PROCESSING',
    'PENDING_APPROVAL',
    'REJECTED',
    'RESUBMITTED',
    'PENDING_PUSH_TO_PARTNER',
    'PUSHED_TO_PARTNER',
    'COMPLETED',
    'CANCELLED',
  ];

  constructor(private fb: FormBuilder, private toastService: ToastService) {}

  ngOnInit(): void {
    this.initializeForm();
    this.populateFormFromChangeRequest();
    this.populateFormFromWorkflowData();
    this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeRequestJsonWorkflowData']?.currentValue) {
      this.populateFormFromWorkflowData();
    }

    if (changes['changeRequest']?.currentValue) {
      this.populateFormFromChangeRequest();
    }
  }

  /**
   * Initialize the restrictions form
   */
  private initializeForm(): void {
    this.restrictionsForm = this.fb.group({
      enabled: [false],
      mode: [BlockStateMode.BLOCK_LISTED],
      states: [[] as string[]],
    });
  }

  private subscribeToFormChanges(): void {
    this.restrictionsForm.valueChanges.subscribe(formValue => {
      const updatedWorkflow = {
        ...this.changeRequestJsonWorkflowData,
        blockNewApplicationsByState: formValue.enabled,
        blockedStatesForNewApplication: formValue.enabled
          ? {
              blockStateMode: formValue.mode,
              blockedStates: formValue.states,
            }
          : null,
      };
      this.workflowUpdated.emit(updatedWorkflow);
    });
  }

  private populateFormFromWorkflowData(): void {
    if (!this.restrictionsForm) return;

    const enabled = this.changeRequestJsonWorkflowData[
      'blockNewApplicationsByState'
    ] as boolean;
    const config = this.changeRequestJsonWorkflowData[
      'blockedStatesForNewApplication'
    ] as IBlockedStatesConfig;

    this.restrictionsForm.patchValue(
      {
        enabled: enabled ?? false,
        mode: config?.blockStateMode ?? BlockStateMode.BLOCK_LISTED,
        states: config?.blockedStates ?? [],
      },
      { emitEvent: false }
    );
  }

  /**
   * Populate form from change request
   */
  private populateFormFromChangeRequest(): void {
    if (!this.restrictionsForm || !this.changeRequest) return;

    const enabled = this.changeRequest.blockNewApplicationsByState;
    const config = this.changeRequest.blockedStatesForNewApplication;

    this.restrictionsForm.patchValue(
      {
        enabled: enabled ?? false,
        mode: config?.blockStateMode ?? BlockStateMode.BLOCK_LISTED,
        states: config?.blockedStates ?? [],
      },
      { emitEvent: false }
    );
  }

  /**
   * Toggle enable/disable
   */
  toggleEnabled(): void {
    const currentValue = this.restrictionsForm.get('enabled')?.value;
    this.restrictionsForm.patchValue({
      enabled: !currentValue,
    });
  }

  /**
   * Change blocking mode
   */
  onModeChange(mode: BlockStateMode): void {
    this.restrictionsForm.patchValue({
      mode: mode,
    });
  }

  /**
   * Add a new state to the blocked states array
   */
  addBlockedState(): void {
    const state = this.newStateInput.trim().toUpperCase();

    if (!state) {
      return;
    }

    const currentStates = this.restrictionsForm.get('states')?.value || [];

    // Check if state already exists
    if (currentStates.includes(state)) {
      this.toastService.show({
        body: 'This state is already in the list',
        type: 'warning',
      });
      return;
    }

    // Add new state
    const updatedStates = [...currentStates, state];
    this.restrictionsForm.patchValue({
      states: updatedStates,
    });

    // Clear input
    this.newStateInput = '';

    this.toastService.show({
      body: `Added state: ${state}`,
      type: 'success',
    });
  }

  /**
   * Remove a state from the blocked states array
   */
  removeBlockedState(state: string): void {
    const currentStates = this.restrictionsForm.get('states')?.value || [];
    const updatedStates = currentStates.filter((s: string) => s !== state);

    this.restrictionsForm.patchValue({
      states: updatedStates,
    });

    this.toastService.show({
      body: `Removed state: ${state}`,
      type: 'info',
    });
  }

  /**
   * Add a suggested state (from common states)
   */
  addSuggestedState(state: string): void {
    const currentStates = this.restrictionsForm.get('states')?.value || [];

    if (currentStates.includes(state)) {
      this.toastService.show({
        body: 'This state is already in the list',
        type: 'warning',
      });
      return;
    }

    const updatedStates = [...currentStates, state];
    this.restrictionsForm.patchValue({
      states: updatedStates,
    });

    this.toastService.show({
      body: `Added state: ${state}`,
      type: 'success',
    });
  }

  /**
   * Check if a state is already added
   */
  isStateAdded(state: string): boolean {
    const currentStates = this.restrictionsForm.get('states')?.value || [];
    return currentStates.includes(state);
  }

  /**
   * Get blocked states as array
   */
  get blockedStates(): string[] {
    return this.restrictionsForm.get('states')?.value || [];
  }

  /**
   * Check if restrictions feature is enabled
   */
  get isRestrictionsEnabled(): boolean {
    return this.restrictionsForm.get('enabled')?.value || false;
  }

  /**
   * Get current mode
   */
  get currentMode(): BlockStateMode {
    return (
      this.restrictionsForm.get('mode')?.value || BlockStateMode.BLOCK_LISTED
    );
  }

  /**
   * Get mode description for display
   */
  getModeDescription(mode: BlockStateMode): string {
    return mode === BlockStateMode.BLOCK_LISTED
      ? 'Users cannot apply if they have applications in ANY of the specified states'
      : 'Users can apply ONLY if ALL their applications are in the specified states';
  }
}
